import { UniqueIdentifier } from "@dnd-kit/core";
import { SortableTree } from "@redotech/arbiter/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/arbiter/navigation/folder-tree/types";
import { flattenTree } from "@redotech/arbiter/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/arbiter/navigation/secondary/redo-secondary-nav-item";
import { OrderTrackingOrdersSystemView } from "@redotech/order-tracking-common/default-order-tracking-order-views";
import { OrderTrackingShipmentsSystemView } from "@redotech/order-tracking-common/default-order-tracking-shipment-views";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import ORDER_TRACKING_ICON from "@redotech/redo-icons/marker-pin-02.svg";
import ORDER_TRACKING_ICON_FILLED from "@redotech/redo-icons/marker-pin-02_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { OrderTrackingOrderViewsContext } from "@redotech/redo-merchant-marketing-app/order-tracking/orders/order-tracking-order-views";
import { OrderTrackingShipmentViewsContext } from "@redotech/redo-merchant-marketing-app/order-tracking/shipments/order-tracking-shipment-views";
import { memo, useContext, useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";

export const OrderTrackingItems = memo(function OrderTrackingItems() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const orderTrackingEnabled = useCapability("orderTracking");

  const trackingBaseUrl = routes.marketingBaseUrl + "/tracking";
  const location = useLocation();
  const anyChildSelected = !!matchPath(
    `${trackingBaseUrl}/*`,
    location.pathname,
  );

  const viewedAboutTrackingPage = localStorage.getItem(
    "viewed_about_tracking_page",
  );

  return (
    <>
      <OrderTrackingAboutItem
        hidden={orderTrackingEnabled}
        isNew={!viewedAboutTrackingPage}
      />
      <OrderTrackingFolderItem
        anyChildSelected={anyChildSelected}
        hidden={!orderTrackingEnabled}
      />
    </>
  );
});

/** Upsell entry point shown before order tracking is enabled. */
const OrderTrackingAboutItem = memo(function OrderTrackingAboutItem({
  hidden,
  isNew,
}: {
  hidden?: boolean;
  isNew: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getTrackingSubRoute("about") }}
      FilledIcon={ORDER_TRACKING_ICON_FILLED}
      friendlyName={t("Order tracking", {
        context: "merchant marketing navigation label",
      })}
      hidden={hidden}
      id="order-tracking-about"
      isNew={isNew}
      UnfilledIcon={ORDER_TRACKING_ICON}
    />
  );
});

const OrderTrackingFolderItem = memo(function OrderTrackingFolderItem({
  anyChildSelected,
  hidden,
}: {
  anyChildSelected: boolean;
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: `${routes.marketingBaseUrl}/tracking` }}
      anyChildSelected={anyChildSelected}
      FilledIcon={ORDER_TRACKING_ICON_FILLED}
      friendlyName={t("Order tracking", {
        context: "merchant marketing navigation label",
      })}
      hidden={hidden}
      id="order-tracking"
      UnfilledIcon={ORDER_TRACKING_ICON}
    >
      <OrderViewsNavItems />
      <ShipmentViewsNavItems />
      <TrackingPagesItem />
    </RedoSecondaryNavItem>
  );
});

const TrackingPagesItem = memo(function TrackingPagesItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getTrackingSubRoute("tracking-page"),
      }}
      friendlyName={t("Tracking pages", {
        context: "merchant marketing navigation label",
      })}
      id="tracking-page"
    />
  );
});

const OrderViewsNavItems = memo(function OrderViewsNavItems() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const viewsContext = useContext(OrderTrackingOrderViewsContext);
  const urlPrefix = routes.getTrackingSubRoute("orders");
  const anyChildSelected = !!matchPath(
    `${urlPrefix}/*`,
    useLocation().pathname,
  );

  const views = useMemo(() => viewsContext?.views ?? [], [viewsContext?.views]);
  const folders = useMemo(
    () => viewsContext?.folders ?? [],
    [viewsContext?.folders],
  );

  const viewsWithoutFolders = useMemo(() => {
    return views.filter((view) => !view.folderId);
  }, [views]);

  const foldersWithViews = useMemo<
    FolderWithView<"order-tracking-orders">[]
  >(() => {
    return folders.map((folder) => ({
      folder,
      views: views.filter((view) => view.folderId === folder._id),
    }));
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    viewsContext?.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"order-tracking-orders">(
      flattenedItems,
      views,
      folders,
    );
    viewsContext?.updateOrdering(result.views, result.folders);
  });

  if (!viewsContext?.hasInitiallyLoaded) {
    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${urlPrefix}/${OrderTrackingOrdersSystemView.ALL}`,
        }}
        anyChildSelected={anyChildSelected}
        friendlyName={t("Orders", {
          context: "merchant marketing navigation label",
        })}
        id="orders"
      />
    );
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${urlPrefix}/${OrderTrackingOrdersSystemView.ALL}`,
      }}
      anyChildSelected={anyChildSelected}
      friendlyName={t("Orders", {
        context: "merchant marketing navigation label",
      })}
      id="orders"
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <OrderViewsCreateFolderItem />
    </RedoSecondaryNavItem>
  );
});

const OrderViewsCreateFolderItem = memo(function OrderViewsCreateFolderItem() {
  const { t } = useTranslation();
  const viewsContext = useContext(OrderTrackingOrderViewsContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "button",
        onClick: () => {
          viewsContext?.openCreateFolderModal();
        },
      }}
      friendlyName={t("+ Add new folder", {
        context: "merchant marketing navigation label",
      })}
      id="orders-create-folder"
    />
  );
});

const ShipmentViewsNavItems = memo(function ShipmentViewsNavItems() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const viewsContext = useContext(OrderTrackingShipmentViewsContext);
  const urlPrefix = routes.getTrackingSubRoute("shipments");
  const anyChildSelected = !!matchPath(
    `${urlPrefix}/*`,
    useLocation().pathname,
  );

  const views = useMemo(() => viewsContext?.views ?? [], [viewsContext?.views]);
  const folders = useMemo(
    () => viewsContext?.folders ?? [],
    [viewsContext?.folders],
  );

  const viewsWithoutFolders = useMemo(() => {
    return views.filter((view) => !view.folderId);
  }, [views]);

  const foldersWithViews = useMemo<
    FolderWithView<"order-tracking-shipments">[]
  >(() => {
    return folders.map((folder) => ({
      folder,
      views: views.filter((view) => view.folderId === folder._id),
    }));
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    viewsContext?.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"order-tracking-shipments">(
      flattenedItems,
      views,
      folders,
    );
    viewsContext?.updateOrdering(result.views, result.folders);
  });

  if (!viewsContext?.hasInitiallyLoaded) {
    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${urlPrefix}/${OrderTrackingShipmentsSystemView.ALL}`,
        }}
        anyChildSelected={anyChildSelected}
        friendlyName={t("Shipments", {
          context: "merchant marketing navigation label",
        })}
        id="shipments"
      />
    );
  }

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: `${urlPrefix}/${OrderTrackingShipmentsSystemView.ALL}`,
      }}
      anyChildSelected={anyChildSelected}
      friendlyName={t("Shipments", {
        context: "merchant marketing navigation label",
      })}
      id="shipments"
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <ShipmentViewsCreateFolderItem />
    </RedoSecondaryNavItem>
  );
});

const ShipmentViewsCreateFolderItem = memo(
  function ShipmentViewsCreateFolderItem() {
    const { t } = useTranslation();
    const viewsContext = useContext(OrderTrackingShipmentViewsContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            viewsContext?.openCreateFolderModal();
          },
        }}
        friendlyName={t("+ Add new folder", {
          context: "merchant marketing navigation label",
        })}
        id="shipments-create-folder"
      />
    );
  },
);
